import { LitElement, html, ifDefined, styles, defineElement } from "@mds/core";
import { buttonCSS } from "./button.styles.js";

/**
 * Button Web Component
 * @slot - This element has a slot
 */
export class MdsButton extends LitElement {
	static get formAssociated() {
		return true;
	}

	static get styles() {
		return [...styles, buttonCSS];
	}

	static get properties() {
		return {
			context: { type: String, reflect: true },
			type: { type: String, reflect: true },
			href: { type: String },
			id: { attribute: "id", type: String },
			name: { attribute: "name", type: String },
			value: { type: String },
			icon: { type: String },
			position: { type: String },
			active: { type: Boolean },
			autofocus: { type: Boolean },
			//	tabindex: { type: String, reflect: true },
			disabled: { type: Boolean, reflect: true },
			compact: { type: Boolean, reflect: true },
			ondark: { type: Boolean }
		};
	}

	constructor() {
		super();
		this.internals = this.attachInternals();

		this.context = "brand";
		this.type = "button";
		this.href = undefined;
		this.value = undefined;
		this.id = undefined;
		this.name = undefined;
		this.position = "start";
		//this.tabindex = '0';
		this.disabled = false;
		this.compact = false;
		this.ondark = false;
		this.icon = undefined;
		this.active = false;
		this.autofocus = false;
	}

	render() {
		const isLink = this.__isLink(this);
		const opts = {
			cls: "mds-btn",
			href: (this.href || isLink) && encodeURI(this.href || "#"),
			style:
				this.icon &&
				`--icon-glyph: var(--mds-icon-glyphs-base-${this.icon.toLowerCase()});--icon-position: ${
					this.position === "start" ? "1" : "-1"
				};`,
			type: this.__addButtonType(),
			disabled: this.disabled
		};
		return isLink
			? this.__anchorTemplate(opts)
			: this.__buttonTemplate(opts);
	}

	/**
	 * @param opts
	 * @returns {TemplateResult<1>}
	 * @private
	 */
	__anchorTemplate(opts) {
		this.disabled = false;
		const { cls, href, style } = opts;
		return html`
			<a
				class="${cls}"
				href="${ifDefined(href)}"
				style="${ifDefined(style)}"
				?autofocus="${this.autofocus}"
			>
				<slot name="text"></slot>
			</a>
		`;
	}
	__click(evt) {
		if (this.disabled) {
			return false;
		} else if (this.type === "link") {
			this.click();
		} else if (this.internals && this.internals.form) {
			const form = this.internals.form;
			this.internals.setFormValue(this.value);
			if (this.type === "submit" && form.checkValidity()) form.submit();
		}

		// Always dispatch click event from mds-button
		// stopPropagation because the click originates from the slotted span[name="text"]
		evt.stopPropagation();
		const event = new Event("click", evt);
		this.dispatchEvent(event);
	}

	/**
	 * @param opts
	 * @returns {TemplateResult<1>}
	 * @private
	 */
	__buttonTemplate(opts) {
		const { cls, type, style, disabled } = opts;
		return html`
			<button
				class="${cls}"
				type="${type}"
				@click="${this.__click}"
				style="${ifDefined(style)}"
				id="${ifDefined(this.id)}"
				name="${ifDefined(this.name)}"
				?disabled="${disabled}"
				?autofocus="${this.autofocus}"
			>
				<slot name="text"></slot>
			</button>
		`;
	}

	/**
	 * @returns {string}
	 * @private
	 */
	__addButtonType() {
		return /submit|button|reset/.test(this.type) ? this.type : "button";
	}

	/**
	 * @returns {boolean|boolean}
	 */
	__isLink() {
		return /cta|news/i.test(this.context) || this.type === "link";
	}
}

defineElement("mds-button", MdsButton);
